import { Component } from '@theme/component';

/**
 * Keeps the compare page's sticky title/vendor/price/rating band and the scrollable
 * product columns below it scrolling horizontally in sync. They have to be two
 * independent overflow-x: auto elements (rather than one nested inside the other) because
 * any ancestor with non-visible overflow breaks position: sticky for descendants relying
 * on the page as their scroll container — see sections/main-compare.liquid.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} stickyBand - The sticky title/vendor/price/rating band.
 * @property {HTMLElement} grid - The scrollable grid of product columns below it.
 *
 * @extends {Component<Refs>}
 */
class CompareStickyBandComponent extends Component {
  requiredRefs = ['stickyBand', 'grid'];

  /**
   * assets/header.js detects its own stuck state with an IntersectionObserver at threshold 1,
   * which only works because the header sticks flush against the viewport's own top edge (so
   * a 1px offset gets clipped by the viewport boundary the observer is watching). Our band
   * sticks *below* the header, comfortably inside the viewport the whole time either way, so
   * that trick has nothing to detect here. Comparing the band's actual rendered position
   * against its own CSS `top` offset works regardless of where that offset is, and re-reads
   * the offset fresh each time — needed since it changes with the header's own sticky state.
   * @type {number | null}
   */
  #scrollRafId = null;

  connectedCallback() {
    super.connectedCallback();

    this.refs.stickyBand.addEventListener('scroll', this.#syncFromStickyBand);
    this.refs.grid.addEventListener('scroll', this.#syncFromGrid);

    window.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    window.addEventListener('resize', this.#checkStuck);
    this.#checkStuck();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.stickyBand.removeEventListener('scroll', this.#syncFromStickyBand);
    this.refs.grid.removeEventListener('scroll', this.#syncFromGrid);
    window.removeEventListener('scroll', this.#handleWindowScroll);
    window.removeEventListener('resize', this.#checkStuck);
    if (this.#scrollRafId !== null) cancelAnimationFrame(this.#scrollRafId);
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#checkStuck();
    });
  };

  #checkStuck = () => {
    const { stickyBand } = this.refs;
    const offset = parseFloat(getComputedStyle(stickyBand).top) || 0;
    stickyBand.toggleAttribute('data-stuck', stickyBand.getBoundingClientRect().top <= offset);
  };

  #syncFromStickyBand = () => {
    const { stickyBand, grid } = this.refs;
    if (grid.scrollLeft !== stickyBand.scrollLeft) grid.scrollLeft = stickyBand.scrollLeft;
  };

  #syncFromGrid = () => {
    const { stickyBand, grid } = this.refs;
    if (stickyBand.scrollLeft !== grid.scrollLeft) stickyBand.scrollLeft = grid.scrollLeft;
  };
}

if (!customElements.get('compare-sticky-band-component')) {
  customElements.define('compare-sticky-band-component', CompareStickyBandComponent);
}
